import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import {
  Thermometer, CheckCircle, AlertTriangle, ChevronLeft, ChevronRight,
  Plus, X, Edit2, Save, Printer, Circle, Check
} from 'lucide-react';

const C = {
  yellow:'#F7EC71', yellowLight:'#FEFCE8', orange:'#E4AC49', orangeLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF', cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3', skyBlue:'#BCD9E5',
  skyBlueLight:'#EAF4F8', skyText:'#3A8FA3', white:'#FFFFFF',
  red:'#DC3545', redLight:'#FFF0F0', amber:'#E07B00', amberLight:'#FFF3E0',
};

const ALL_VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen'];

const SHIFT_LABELS = {
  'opening':   'Opening',
  'mid-shift': 'Mid-Shift',
  'closing':   'Closing',
};

const SHIFT_TAB = {
  'opening':   { active: '#E8923A', activeBg: '#FDF3E7', activeText: '#7A3D00' },
  'mid-shift': { active: C.skyText, activeBg: C.skyBlueLight, activeText: '#1A5F72' },
  'closing':   { active: C.navy,    activeBg: '#EEF1F5',      activeText: C.navy    },
};

const TYPE_CONFIG = {
  fridge:     { label: 'Fridge',     color: C.skyText, bg: C.skyBlueLight },
  freezer:    { label: 'Freezer',    color: C.navy,    bg: '#eef1f5'      },
  dishwasher: { label: 'Dishwasher', color: C.amber,   bg: C.amberLight   },
};

function getTempStatus(fridge, temp) {
  if (fridge.type === 'dishwasher') {
    return fridge.min_temp != null ? (temp >= fridge.min_temp ? 'good' : 'bad') : 'good';
  }
  if (fridge.max_temp == null) return 'good';
  if (temp > fridge.max_temp) return 'bad';
  if (fridge.min_temp != null && temp < fridge.min_temp) return 'bad';
  const pct       = fridge.amber_threshold_pct ?? 0.20;
  const amberEdge = fridge.max_temp - pct * fridge.max_temp;
  if (temp >= amberEdge) return 'amber';
  return 'good';
}

function statusColors(status) {
  if (status === 'good')  return { color: C.green, bg: C.greenLight };
  if (status === 'amber') return { color: C.amber, bg: C.amberLight };
  return                         { color: C.red,   bg: C.redLight   };
}

function inRange(fridge, temp) {
  if (fridge.type === 'dishwasher') return fridge.min_temp != null ? temp >= fridge.min_temp : true;
  if (fridge.max_temp != null && temp > fridge.max_temp) return false;
  if (fridge.min_temp != null && temp < fridge.min_temp) return false;
  return true;
}

function getPresets(fridge) {
  if (fridge.type === 'freezer') return [-18, -19, -20, -21, -22, -23, -24];
  if (fridge.type === 'dishwasher') {
    const base = fridge.min_temp || 60;
    return [base, base + 2, base + 5, base + 8, base + 10, base + 15];
  }
  const max = fridge.max_temp || 7;
  const presets = [];
  for (let t = 1; t <= max; t++) presets.push(t);
  return presets;
}

function toLocalISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function today() { return toLocalISODate(new Date()); }

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) +
    ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function weekStart(offset = 0) {
  const d = new Date();
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1 + offset * 7);
  d.setHours(0, 0, 0, 0);
  return d;
}

function rangeLabel(fridge) {
  if (fridge.type === 'dishwasher') return `min ${fridge.min_temp}°C`;
  if (fridge.max_temp != null)      return `max ${fridge.max_temp}°C`;
  return '';
}

function TempBadge({ temp, status }) {
  const sc = statusColors(status);
  return (
    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: sc.color, background: sc.bg, padding: '0.15rem 0.5rem', borderRadius: '999px' }}>
      {temp}°C
    </span>
  );
}

// ─── Preset temp picker ───────────────────────────────────────────────────────
function TempPicker({ fridge, value, onChange, onSave, isSaving, isSaved, needsAction }) {
  const isDishwasher = fridge.type === 'dishwasher';
  const isFreezer    = fridge.type === 'freezer';
  const presets      = getPresets(fridge);
  const DECIMALS     = [0.0, 0.2, 0.4, 0.6, 0.8];

  const parsed  = value !== '' ? parseFloat(value) : null;
  const intPart = parsed !== null && !isNaN(parsed)
    ? (isFreezer ? Math.ceil(parsed) : Math.floor(parsed))
    : null;
  const decPart = parsed !== null && !isNaN(parsed) && !isDishwasher
    ? Math.round((Math.abs(parsed) - Math.abs(isFreezer ? Math.ceil(parsed) : Math.floor(parsed))) * 10) / 10
    : null;

  function pickInt(t) {
    if (isDishwasher) { onChange(String(t)); return; }
    if (decPart !== null) {
      const combined = isFreezer ? t - decPart : t + decPart;
      onChange(String(Math.round(combined * 10) / 10));
    } else {
      onChange(`${t}_int`);
    }
  }

  function pickDec(d) {
    const base = intPart;
    if (base === null) return;
    const combined = isFreezer ? base - d : base + d;
    onChange(String(Math.round(combined * 10) / 10));
  }

  const intSelected  = intPart !== null || value.endsWith('_int');
  const intValue     = value.endsWith('_int') ? parseInt(value) : intPart;
  const decSelected  = !isDishwasher && decPart !== null && !value.endsWith('_int');
  const fullSelected = isDishwasher ? intSelected : (intSelected && decSelected);
  const finalTemp    = fullSelected && !value.endsWith('_int') ? parsed : null;
  const status       = finalTemp !== null ? getTempStatus(fridge, finalTemp) : null;
  const sc           = status ? statusColors(status) : null;
  const canLog       = fullSelected && !value.endsWith('_int') && !needsAction;

  return (
    <div style={{ width: '100%' }}>

      {/* ── Row 1: Integer presets ── */}
      <div style={{ marginBottom: isDishwasher ? '0' : '0.4rem' }}>
        <div style={{ fontSize: '0.6rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.22rem' }}>
          {isDishwasher ? 'Temperature °C' : 'Whole number'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.28rem', alignItems: 'center' }}>
          {presets.map(t => {
            const isSelected  = intValue === t && intSelected;
            const previewTemp = decSelected ? (isFreezer ? t - decPart : t + decPart) : t;
            const s   = getTempStatus(fridge, previewTemp);
            const sc2 = statusColors(s);
            return (
              <button key={t} onClick={() => pickInt(t)} style={{
                padding: '0.3rem 0.55rem', borderRadius: '7px', fontSize: '0.82rem',
                fontWeight: isSelected ? 800 : 500, cursor: 'pointer',
                border: `2px solid ${isSelected ? sc2.color : C.border}`,
                background: isSelected ? sc2.bg : C.white,
                color: isSelected ? sc2.color : C.mid,
                transition: 'all 0.1s',
              }}>
                {t}°
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Row 2: Decimal presets + preview badge + Log Temp button ── */}
      {!isDishwasher && (
        <div style={{ marginBottom: '0.25rem' }}>
          <div style={{ fontSize: '0.6rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.22rem' }}>
            Decimal
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.28rem', alignItems: 'center' }}>
            {DECIMALS.map(d => {
              const isSelected  = decSelected && decPart === d;
              const previewTemp = intValue !== null
                ? Math.round((isFreezer ? intValue - d : intValue + d) * 10) / 10
                : null;
              const s   = previewTemp !== null ? getTempStatus(fridge, previewTemp) : null;
              const sc2 = s ? statusColors(s) : null;
              const disabled = !intSelected;
              return (
                <button key={d} onClick={() => !disabled && pickDec(d)} style={{
                  padding: '0.3rem 0.55rem', borderRadius: '7px', fontSize: '0.82rem',
                  fontWeight: isSelected ? 800 : 500,
                  cursor: disabled ? 'default' : 'pointer',
                  border: `2px solid ${isSelected && sc2 ? sc2.color : disabled ? C.cream : C.border}`,
                  background: isSelected && sc2 ? sc2.bg : disabled ? C.cream : C.white,
                  color: isSelected && sc2 ? sc2.color : disabled ? C.border : C.mid,
                  transition: 'all 0.1s',
                  opacity: disabled ? 0.5 : 1,
                }}>
                  .{Math.round(d * 10)}
                </button>
              );
            })}

            {/* Divider */}
            <div style={{ width: '1px', height: '20px', background: C.border, margin: '0 0.1rem', flexShrink: 0 }} />

            {/* Preview badge */}
            {finalTemp !== null && sc ? (
              <div style={{ padding: '0.28rem 0.65rem', borderRadius: '8px', background: sc.bg, border: `1.5px solid ${sc.color}44`, fontSize: '0.9rem', fontWeight: 800, color: sc.color, letterSpacing: '-0.01em', flexShrink: 0 }}>
                {finalTemp}°C
              </div>
            ) : (
              <div style={{ padding: '0.28rem 0.65rem', borderRadius: '8px', background: C.cream, border: `1.5px solid ${C.border}`, fontSize: '0.75rem', fontWeight: 500, color: C.border, flexShrink: 0 }}>
                —.—
              </div>
            )}

            {/* Log Temp — only when fully selected */}
            {fullSelected && !value.endsWith('_int') && (
              <button onClick={onSave} disabled={!canLog || isSaving} style={{
                padding: '0.32rem 0.95rem', borderRadius: '7px', border: 'none',
                background: isSaved ? C.green : canLog ? C.navy : C.cream,
                color: canLog ? C.white : C.mid,
                fontSize: '0.82rem', fontWeight: 700,
                cursor: canLog ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', gap: '0.3rem',
                transition: 'all 0.15s', flexShrink: 0,
              }}>
                {isSaved ? <><Check size={12} /> Logged</> : isSaving ? '…' : 'Log Temp'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Dishwasher log button */}
      {isDishwasher && fullSelected && (
        <div style={{ marginTop: '0.5rem' }}>
          <button onClick={onSave} disabled={!canLog || isSaving} style={{
            padding: '0.32rem 0.95rem', borderRadius: '7px', border: 'none',
            background: isSaved ? C.green : canLog ? C.navy : C.cream,
            color: canLog ? C.white : C.mid,
            fontSize: '0.82rem', fontWeight: 700,
            cursor: canLog ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', gap: '0.3rem',
          }}>
            {isSaved ? <><Check size={12} /> Logged</> : isSaving ? '…' : 'Log Temp'}
          </button>
        </div>
      )}

      {/* ── Out of range warning ── */}
      {status === 'bad' && (
        <div style={{ marginTop: '0.45rem', padding: '0.4rem 0.65rem', background: C.redLight, borderRadius: '7px', borderLeft: `3px solid ${C.red}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.25rem' }}>
            <AlertTriangle size={11} style={{ color: C.red }} />
            <span style={{ fontSize: '0.68rem', color: C.red, fontWeight: 600 }}>Out of range — describe action taken</span>
          </div>
          <span style={{ fontSize: '0.68rem', color: C.red }}>Enter corrective action below before logging</span>
        </div>
      )}
    </div>
  );
}

function getShiftsFor(venue, dept) {
  if (dept !== 'kitchen') return ['opening', 'closing'];
  if (venue === 'Box Plantage') return ['opening', 'closing'];
  return ['opening', 'mid-shift', 'closing'];
}

// ─── FRIDGE TEMPS SECTION (used in Checklist.js) ─────────────────────────────
export function FridgeTempsSection({ venue, dept, currentUser }) {
  const shifts = getShiftsFor(venue, dept);

  const [activeShift, setActiveShift] = useState(shifts[0]);
  const [fridges,     setFridges]     = useState([]);
  const [existing,    setExisting]    = useState({});
  const [inputs,      setInputs]      = useState({});
  const [actions,     setActions]     = useState({});
  const [saving,      setSaving]      = useState({});
  const [saved,       setSaved]       = useState({});
  const [loading,     setLoading]     = useState(true);

  const dateStr = today();

  const loadData = useCallback(async () => {
    setLoading(true);
    const { data: fridgeData } = await supabase
      .from('fridges').select('*')
      .eq('venue', venue).eq('dept', dept).eq('active', true)
      .order('sort_order');

    const { data: logData } = await supabase
      .from('fridge_temp_logs').select('*')
      .eq('venue', venue).eq('dept', dept).eq('shift', activeShift)
      .gte('logged_at', dateStr + 'T00:00:00')
      .lte('logged_at', dateStr + 'T23:59:59');

    const existingMap = {};
    (logData || []).forEach(row => { existingMap[row.fridge_id] = row; });
    setFridges(fridgeData || []);
    setExisting(existingMap);
    setInputs({});
    setActions({});
    setLoading(false);
  }, [venue, dept, activeShift, dateStr]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleSave(fridge) {
    const rawVal = inputs[fridge.id];
    if (rawVal === undefined || rawVal === '') return;
    const temp = parseFloat(rawVal);
    if (isNaN(temp)) return;
    const ok = inRange(fridge, temp);
    if (!ok && !actions[fridge.id]?.trim()) return;

    setSaving(prev => ({ ...prev, [fridge.id]: true }));
    const payload = {
      fridge_id: fridge.id, venue, dept, shift: activeShift,
      temp_celsius: temp, in_range: ok,
      logged_by: currentUser?.name || null,
      logged_at: new Date().toISOString(),
      corrective_action: !ok ? (actions[fridge.id]?.trim() || null) : null,
    };
    if (existing[fridge.id]) {
      await supabase.from('fridge_temp_logs').delete().eq('id', existing[fridge.id].id);
    }
    const { data } = await supabase.from('fridge_temp_logs').insert(payload).select().single();
    if (data) {
      setExisting(prev => ({ ...prev, [fridge.id]: data }));
      setInputs(prev => { const n = { ...prev }; delete n[fridge.id]; return n; });
      setSaved(prev => ({ ...prev, [fridge.id]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [fridge.id]: false })), 2000);
    }
    setSaving(prev => ({ ...prev, [fridge.id]: false }));
  }

  const allDone    = fridges.length > 0 && fridges.every(f => existing[f.id]);
  const outOfRange = Object.values(existing).filter(e => !e.in_range).length;

  if (loading) return <p style={{ color: C.mid, fontSize: '0.85rem' }}>Loading fridges…</p>;

  if (fridges.length === 0) return (
    <div style={{ textAlign: 'center', padding: '2rem', color: C.mid }}>
      <Thermometer size={28} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
      <p style={{ fontSize: '0.85rem' }}>No fridges configured for {venue} {dept}.</p>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
        {shifts.map(s => {
          const tab = SHIFT_TAB[s];
          const isActive = activeShift === s;
          return (
            <button key={s} onClick={() => setActiveShift(s)} style={{
              flex: 1, padding: '0.4rem 0', borderRadius: '9px', fontSize: '0.82rem',
              fontWeight: isActive ? 700 : 500, cursor: 'pointer',
              border: `2px solid ${isActive ? tab.active : C.border}`,
              background: isActive ? tab.activeBg : C.white,
              color: isActive ? tab.activeText : C.mid,
              transition: 'all 0.15s',
            }}>
              {SHIFT_LABELS[s]}
            </button>
          );
        })}
      </div>

      {allDone && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', marginBottom: '0.85rem', background: outOfRange > 0 ? C.amberLight : C.greenLight, borderRadius: '10px', border: `1px solid ${outOfRange > 0 ? C.amber + '44' : C.green + '44'}` }}>
          {outOfRange > 0
            ? <AlertTriangle size={14} style={{ color: C.amber }} />
            : <CheckCircle size={14} style={{ color: C.green }} />}
          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: outOfRange > 0 ? C.amber : C.green }}>
            {outOfRange > 0
              ? `All logged · ${outOfRange} out of range — corrective actions recorded`
              : `All ${SHIFT_LABELS[activeShift]} temps logged ✓`}
          </span>
        </div>
      )}

      {fridges.map(fridge => {
        const log        = existing[fridge.id];
        const isSaved    = saved[fridge.id];
        const isSaving   = saving[fridge.id];
        const val        = inputs[fridge.id] ?? '';
        const parsedTemp = parseFloat(val);
        const tempStatus = val !== '' && !isNaN(parsedTemp) ? getTempStatus(fridge, parsedTemp) : null;
        const typeCfg    = TYPE_CONFIG[fridge.type] || TYPE_CONFIG.fridge;
        const needsAction = tempStatus === 'bad' && !actions[fridge.id]?.trim();
        const isEditing  = inputs[fridge.id] !== undefined;
        const logStatus  = log ? getTempStatus(fridge, log.temp_celsius) : null;
        const logSC      = logStatus ? statusColors(logStatus) : null;
        const borderColor = log ? (logSC?.color || C.border) : C.border;

        return (
          <div key={fridge.id} style={{ background: C.white, borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: '0.5rem', borderLeft: `4px solid ${borderColor}`, padding: '0.6rem 0.85rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.58rem', fontWeight: 700, color: typeCfg.color, background: typeCfg.bg, padding: '0.08rem 0.35rem', borderRadius: '999px', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>
                {typeCfg.label}
              </span>
              <span style={{ fontSize: '0.88rem', fontWeight: 700, color: C.charcoal }}>{fridge.name}</span>
              <span style={{ fontSize: '0.6rem', color: C.mid, flexShrink: 0 }}>{rangeLabel(fridge)}</span>
              {log && !isEditing && (
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                  <TempBadge temp={log.temp_celsius} status={logStatus} />
                  <span style={{ fontSize: '0.62rem', color: C.mid }}>{formatDateTime(log.logged_at)}</span>
                  <button onClick={() => setInputs(prev => ({ ...prev, [fridge.id]: String(log.temp_celsius) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid, padding: '0.1rem' }}>
                    <Edit2 size={12} />
                  </button>
                </div>
              )}
            </div>

            {(!log || isEditing) && (
              <div style={{ marginTop: '0.5rem' }}>
                <TempPicker
                  fridge={fridge}
                  value={val}
                  onChange={v => setInputs(prev => ({ ...prev, [fridge.id]: v }))}
                  onSave={() => handleSave(fridge)}
                  isSaving={isSaving}
                  isSaved={isSaved}
                  needsAction={needsAction}
                />
                {tempStatus === 'bad' && (
                  <div style={{ marginTop: '0.4rem' }}>
                    <input value={actions[fridge.id] || ''} onChange={e => setActions(prev => ({ ...prev, [fridge.id]: e.target.value }))}
                      placeholder="Corrective action e.g. Moved stock, called engineer"
                      style={{ width: '100%', padding: '0.35rem 0.55rem', border: `1px solid ${C.red}55`, borderRadius: '7px', fontSize: '0.8rem', color: C.charcoal, background: C.redLight, boxSizing: 'border-box' }} />
                  </div>
                )}
                {isEditing && log && (
                  <button onClick={() => setInputs(prev => { const n = { ...prev }; delete n[fridge.id]; return n; })} style={{ marginTop: '0.35rem', padding: '0.25rem 0.65rem', borderRadius: '7px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, fontSize: '0.78rem', cursor: 'pointer' }}>
                    Cancel
                  </button>
                )}
              </div>
            )}

            {log && !log.in_range && !isEditing && (
              <div style={{ marginTop: '0.35rem', padding: '0.3rem 0.55rem', background: C.amberLight, borderRadius: '6px', borderLeft: `3px solid ${C.amber}` }}>
                <span style={{ fontSize: '0.68rem', color: C.amber, fontWeight: 600 }}>Action: </span>
                <span style={{ fontSize: '0.68rem', color: C.charcoal }}>{log.corrective_action || '—'}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Cell colour helper for Reports grid ──────────────────────────────────────
function getGridCellStyle(fridge, temp) {
  if (temp == null) return { background: 'transparent', color: C.border };
  const status = getTempStatus(fridge, temp);
  const sc     = statusColors(status);
  return { background: sc.bg, color: sc.color };
}

// ─── FRIDGE TEMPS TAB (used in Reports.js) ───────────────────────────────────
export function FridgeTempsTab({ currentUser }) {
  const [weekOffset,  setWeekOffset]  = useState(0);
  const [venueFilter, setVenueFilter] = useState(currentUser?.venue || 'All');
  const [deptFilter,  setDeptFilter]  = useState('All');
  const [fridges,     setFridges]     = useState([]);
  const [logs,        setLogs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(null);
  const [printing,    setPrinting]    = useState(false);

  const wStart = weekStart(weekOffset);
  const days   = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(wStart); d.setDate(d.getDate() + i); return d;
  });
  const weekLabel = `${days[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${days[6].toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  useEffect(() => {
    async function load() {
      setLoading(true);
      let fq = supabase.from('fridges').select('*').eq('active', true).order('sort_order');
      if (venueFilter !== 'All') fq = fq.eq('venue', venueFilter);
      if (deptFilter  !== 'All') fq = fq.eq('dept', deptFilter);
      const from = toLocalISODate(days[0]) + 'T00:00:00';
      const to   = toLocalISODate(days[6]) + 'T23:59:59';
      let lq = supabase.from('fridge_temp_logs').select('*').gte('logged_at', from).lte('logged_at', to);
      if (venueFilter !== 'All') lq = lq.eq('venue', venueFilter);
      if (deptFilter  !== 'All') lq = lq.eq('dept', deptFilter);
      const [{ data: fd }, { data: ld }] = await Promise.all([fq, lq]);
      setFridges(fd || []); setLogs(ld || []); setLoading(false);
    }
    load();
  }, [weekOffset, venueFilter, deptFilter]); // eslint-disable-line

  function getLog(fridgeId, dateStr, shift) {
    return logs.find(l => l.fridge_id === fridgeId && l.logged_at.slice(0, 10) === dateStr && l.shift === shift) || null;
  }

  function handlePrintGrid() {
    setPrinting(true);

    const groupBlocks = Object.entries(grouped).map(([groupKey, groupFridges]) => {
      const dept   = groupFridges[0].dept;
      const venue  = groupFridges[0].venue;
      const shifts = getShiftsFor(venue, dept);

      const dayHeaderCells = days.map(d => {
        const label = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
        return `<th colspan="${shifts.length}" class="dayhead">${label}</th>`;
      }).join('');

      const shiftHeaderCells = days.map(() =>
        shifts.map(s => `<td class="shifthead">${s === 'mid-shift' ? 'Mid' : s.slice(0, 3)}</td>`).join('')
      ).join('');

      const bodyRows = groupFridges.map((fridge, fi) => {
        const cells = days.map(d => {
          const ds = toLocalISODate(d);
          return shifts.map(s => {
            const log = getLog(fridge.id, ds, s);
            if (!log) return `<td class="cell empty">—</td>`;
            const style = getGridCellStyle(fridge, log.temp_celsius);
            return `<td class="cell" style="background:${style.background};color:${style.color};font-weight:700;">${log.temp_celsius}°</td>`;
          }).join('');
        }).join('');
        return `<tr class="${fi % 2 === 0 ? 'even' : 'odd'}"><td class="fridgecell"><strong>${fridge.name}</strong><span class="rangesub">${rangeLabel(fridge)}</span></td>${cells}</tr>`;
      }).join('');

      return `
        <div class="group">
          <p class="grouptitle">${groupKey}</p>
          <table>
            <thead>
              <tr><th class="fridgehead"></th>${dayHeaderCells}</tr>
              <tr><td></td>${shiftHeaderCells}</tr>
            </thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </div>`;
    }).join('');

    // Genuine flagged incidents only — a fridge went out of range and a real
    // corrective action was recorded. Nothing else appears here.
    const fridgeById = Object.fromEntries(fridges.map(f => [f.id, f]));
    const incidents = logs
      .filter(l => !l.in_range && l.corrective_action)
      .sort((a, b) => new Date(a.logged_at) - new Date(b.logged_at))
      .map(l => {
        const f = fridgeById[l.fridge_id];
        return `<tr>
          <td>${f ? f.name : '—'}</td>
          <td>${f ? f.venue : ''}</td>
          <td>${formatDateTime(l.logged_at)}</td>
          <td>${SHIFT_LABELS[l.shift] || l.shift}</td>
          <td style="font-weight:700;color:#DC3545;">${l.temp_celsius}°C</td>
          <td>${l.corrective_action}</td>
        </tr>`;
      }).join('');

    const incidentsBlock = incidents ? `
      <div class="incidents">
        <p class="grouptitle">Corrective Actions This Week</p>
        <table>
          <thead><tr>
            <th class="ihead">Fridge</th><th class="ihead">Venue</th><th class="ihead">Time</th>
            <th class="ihead">Shift</th><th class="ihead">Temp</th><th class="ihead">Action Taken</th>
          </tr></thead>
          <tbody>${incidents}</tbody>
        </table>
      </div>` : '';

    const html = `<html><head><title>HACCP Temperature Log — ${weekLabel}</title>
      <style>
        @page { size: landscape; margin: 10mm; }
        body { font-family: sans-serif; padding: 0; color: #2F3A42; }
        h2 { margin: 0 0 2px; font-size: 15px; }
        p.sub { color: #666; margin: 2px 0 10px; font-size: 10px; }
        .group { page-break-inside: avoid; margin-bottom: 10px; }
        .grouptitle { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #7A8A96; margin: 0 0 3px; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .fridgehead { width: 130px; }
        .fridgecell { padding: 2px 5px; border-bottom: 1px solid #eee; white-space: nowrap; font-size: 8.5px; }
        .rangesub { display: block; font-weight: 400; color: #7A8A96; font-size: 7px; }
        .dayhead { background: #445366; color: white; padding: 2px 2px; font-size: 7.5px; text-transform: uppercase; letter-spacing: 0.02em; border-left: 1px solid rgba(255,255,255,0.2); }
        .shifthead { text-align: center; padding: 1px 1px; font-size: 6.5px; color: #7A8A96; border-bottom: 1px solid #D8DEE3; white-space: nowrap; }
        .cell { text-align: center; padding: 2px 1px; font-size: 8px; border-bottom: 1px solid #eee; }
        .cell.empty { color: #D8DEE3; }
        tr.even td.fridgecell { background: #F7F6EF; }
        .incidents { page-break-inside: avoid; margin-top: 10px; }
        .ihead { background: #445366; color: white; padding: 3px 6px; text-align: left; font-size: 8px; text-transform: uppercase; letter-spacing: 0.03em; }
        .incidents td { padding: 3px 6px; border-bottom: 1px solid #eee; font-size: 8.5px; }
        @media print { button { display: none; } }
      </style></head>
      <body>
        <h2>🍋 HACCP Temperature Log</h2>
        <p class="sub">${venueFilter !== 'All' ? venueFilter : 'All Venues'} · ${deptFilter !== 'All' ? deptFilter : 'All Depts'} · Week of ${weekLabel} · Printed ${new Date().toLocaleString('en-GB')}</p>
        ${groupBlocks}
        ${incidentsBlock}
      </body></html>`;

    const w = window.open('', '_blank');
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { w.print(); }, 400);
    setPrinting(false);
  }

  const grouped = fridges.reduce((acc, f) => {
    const key = `${f.venue} · ${f.dept}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(f);
    return acc;
  }, {});

  return (
    <div>
      {selected && (() => {
        const logStatus = selected.log ? getTempStatus(selected.fridge, selected.log.temp_celsius) : null;
        const logSC     = logStatus ? statusColors(logStatus) : null;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(47,58,66,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
            onClick={e => { if (e.target === e.currentTarget) setSelected(null); }}>
            <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '400px', padding: '1.5rem', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: C.charcoal }}>{selected.fridge.name}</h3>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={16} /></button>
              </div>
              {selected.log ? (
                <>
                  <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div style={{ flex: 1, padding: '0.75rem', background: logSC?.bg, borderRadius: '10px', textAlign: 'center' }}>
                      <p style={{ margin: '0 0 0.15rem', fontSize: '0.62rem', fontWeight: 700, color: logSC?.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Temperature</p>
                      <p style={{ margin: 0, fontSize: '1.9rem', fontWeight: 900, color: logSC?.color }}>{selected.log.temp_celsius}°C</p>
                    </div>
                    <div style={{ flex: 1, padding: '0.75rem', background: C.cream, borderRadius: '10px', textAlign: 'center' }}>
                      <p style={{ margin: '0 0 0.15rem', fontSize: '0.62rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</p>
                      <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: 700, color: logSC?.color }}>
                        {logStatus === 'good' ? '✓ In range' : logStatus === 'amber' ? '⚠ Near limit' : '✗ Out of range'}
                      </p>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: C.mid, marginBottom: '0.5rem' }}>
                    {[['Shift', SHIFT_LABELS[selected.log.shift]||selected.log.shift],['Time',formatDateTime(selected.log.logged_at)],['Range',rangeLabel(selected.fridge)]].map(([label,val]) => (
                      <p key={label} style={{ margin: '0 0 0.22rem' }}><strong style={{ color: C.charcoal }}>{label}:</strong> {val}</p>
                    ))}
                  </div>
                  {!selected.log.in_range && selected.log.corrective_action && (
                    <div style={{ padding: '0.6rem 0.75rem', background: C.amberLight, borderRadius: '9px', borderLeft: `3px solid ${C.amber}` }}>
                      <p style={{ margin: '0 0 0.18rem', fontSize: '0.62rem', fontWeight: 700, color: C.amber, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Corrective Action</p>
                      <p style={{ margin: 0, fontSize: '0.83rem', color: C.charcoal }}>{selected.log.corrective_action}</p>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '1.5rem', color: C.mid }}>
                  <Circle size={28} style={{ opacity: 0.25, marginBottom: '0.5rem' }} />
                  <p style={{ fontSize: '0.85rem' }}>Not logged for this shift.</p>
                </div>
              )}
              <button onClick={() => setSelected(null)} style={{ marginTop: '1rem', width: '100%', padding: '0.5rem', background: C.navy, color: C.white, border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>Close</button>
            </div>
          </div>
        );
      })()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.85rem' }}>
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {['All', ...ALL_VENUES].map(v => (
            <button key={v} onClick={() => setVenueFilter(v)} style={{ padding: '0.26rem 0.6rem', borderRadius: '7px', fontSize: '0.74rem', border: `1.5px solid ${venueFilter === v ? C.navy : C.border}`, background: venueFilter === v ? C.navy : C.white, color: venueFilter === v ? C.white : C.mid, fontWeight: venueFilter === v ? 600 : 400, cursor: 'pointer' }}>
              {v === 'All' ? 'All Venues' : v}
            </button>
          ))}
          <div style={{ width: '1px', height: '16px', background: C.border, margin: '0 0.1rem' }} />
          {['All', 'bar', 'kitchen'].map(d => (
            <button key={d} onClick={() => setDeptFilter(d)} style={{ padding: '0.26rem 0.6rem', borderRadius: '7px', fontSize: '0.74rem', border: `1.5px solid ${deptFilter === d ? C.skyText : C.border}`, background: deptFilter === d ? C.skyBlueLight : C.white, color: deptFilter === d ? C.skyText : C.mid, fontWeight: deptFilter === d ? 600 : 400, cursor: 'pointer', textTransform: 'capitalize' }}>
              {d === 'All' ? 'All Depts' : d}
            </button>
          ))}
        </div>
        <button onClick={handlePrintGrid} disabled={printing} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.28rem 0.75rem', border: `1px solid ${C.border}`, borderRadius: '8px', background: C.white, color: C.mid, fontSize: '0.74rem', cursor: printing ? 'default' : 'pointer', whiteSpace: 'nowrap', opacity: printing ? 0.6 : 1 }}>
          <Printer size={12} /> {printing ? 'Preparing…' : 'Print Week'}
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.1rem' }}>
        <button onClick={() => setWeekOffset(o => o - 1)} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.28rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', background: C.white, color: C.mid, fontSize: '0.76rem', cursor: 'pointer' }}>
          <ChevronLeft size={13} /> Prev
        </button>
        <span style={{ flex: 1, textAlign: 'center', fontSize: '0.82rem', fontWeight: 600, color: C.charcoal }}>{weekLabel}</span>
        <button onClick={() => setWeekOffset(o => o + 1)} disabled={weekOffset >= 0} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.28rem 0.65rem', border: `1px solid ${weekOffset >= 0 ? C.cream : C.border}`, borderRadius: '8px', background: weekOffset >= 0 ? C.cream : C.white, color: weekOffset >= 0 ? C.border : C.mid, fontSize: '0.76rem', cursor: weekOffset >= 0 ? 'default' : 'pointer' }}>
          Next <ChevronRight size={13} />
        </button>
      </div>

      {loading ? (
        <p style={{ color: C.mid, fontSize: '0.85rem' }}>Loading…</p>
      ) : fridges.length === 0 ? (
        <p style={{ color: C.mid, fontSize: '0.85rem' }}>No fridges found.</p>
      ) : (
        Object.entries(grouped).map(([groupKey, groupFridges]) => {
          const dept   = groupFridges[0].dept;
          const venue  = groupFridges[0].venue;
          const shifts = getShiftsFor(venue, dept);
          return (
            <div key={groupKey} style={{ marginBottom: '2rem' }}>
              <p style={{ margin: '0 0 0.6rem', fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {groupKey}
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', minWidth: '580px' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '0.38rem 0.5rem', color: C.mid, fontWeight: 600, fontSize: '0.67rem', borderBottom: `2px solid ${C.border}`, minWidth: '130px' }}>Fridge</th>
                      {days.map(d => {
                        const isToday = d.toDateString() === new Date().toDateString();
                        return (
                          <th key={String(d)} colSpan={shifts.length} style={{ textAlign: 'center', padding: '0.32rem 0.2rem', color: isToday ? C.navy : C.mid, fontWeight: isToday ? 700 : 500, fontSize: '0.67rem', borderBottom: `2px solid ${C.border}`, borderLeft: `1px solid ${C.border}`, background: isToday ? C.cream : 'transparent' }}>
                            {d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' })}
                          </th>
                        );
                      })}
                    </tr>
                    <tr>
                      <td style={{ borderBottom: `1px solid ${C.border}` }} />
                      {days.map(d =>
                        shifts.map(s => (
                          <td key={`${d}-${s}`} style={{ textAlign: 'center', padding: '0.15rem 0.1rem', fontSize: '0.57rem', color: C.mid, fontWeight: 500, borderBottom: `1px solid ${C.border}`, borderLeft: s === shifts[0] ? `1px solid ${C.border}` : 'none', whiteSpace: 'nowrap' }}>
                            {s === 'mid-shift' ? 'mid' : s.slice(0,3)}
                          </td>
                        ))
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {groupFridges.map((fridge, fi) => (
                      <tr key={fridge.id} style={{ background: fi % 2 === 0 ? C.white : C.cream }}>
                        <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600, color: C.charcoal, whiteSpace: 'nowrap', borderBottom: `1px solid ${C.border}` }}>
                          {fridge.name}
                          <span style={{ display: 'block', fontSize: '0.6rem', color: C.mid, fontWeight: 400 }}>{rangeLabel(fridge)}</span>
                        </td>
                        {days.map(d => {
                          const ds = toLocalISODate(d);
                          return shifts.map(s => {
                            const log = getLog(fridge.id, ds, s);
                            const cellStyle = log ? getGridCellStyle(fridge, log.temp_celsius) : { background: 'transparent', color: C.border };
                            return (
                              <td key={`${d}-${s}`} onClick={() => setSelected({ fridge, date: ds, shift: s, log })}
                                style={{ textAlign: 'center', padding: '0.26rem 0.15rem', borderBottom: `1px solid ${C.border}`, borderLeft: s === shifts[0] ? `1px solid ${C.border}` : 'none', cursor: 'pointer', background: cellStyle.background, transition: 'opacity 0.1s' }}>
                                {log ? (
                                  <span style={{ fontWeight: 700, fontSize: '0.7rem', color: cellStyle.color }}>{log.temp_celsius}°</span>
                                ) : (
                                  <span style={{ color: C.border, fontSize: '0.6rem' }}>—</span>
                                )}
                              </td>
                            );
                          });
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ─── FRIDGE TEMPS PAGE (admin — fridge management) ───────────────────────────
export default function FridgeTempsPage({ currentUser }) {
  const [fridges,  setFridges]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);

  const blank = { venue: ALL_VENUES[0], dept: 'bar', name: '', type: 'fridge', min_temp: '', max_temp: '', sort_order: 0, amber_threshold_pct: 0.20 };
  const [form, setForm] = useState(blank);

  useEffect(() => { loadFridges(); }, []);

  async function loadFridges() {
    setLoading(true);
    const { data } = await supabase.from('fridges').select('*').order('venue').order('dept').order('sort_order');
    setFridges(data || []); setLoading(false);
  }

  function startEdit(fridge) {
    setEditing(fridge.id);
    setForm({ venue: fridge.venue, dept: fridge.dept, name: fridge.name, type: fridge.type, min_temp: fridge.min_temp ?? '', max_temp: fridge.max_temp ?? '', sort_order: fridge.sort_order ?? 0, amber_threshold_pct: fridge.amber_threshold_pct ?? 0.20 });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      venue: form.venue, dept: form.dept, name: form.name.trim(), type: form.type,
      min_temp: form.min_temp !== '' ? parseFloat(form.min_temp) : null,
      max_temp: form.max_temp !== '' ? parseFloat(form.max_temp) : null,
      sort_order: parseInt(form.sort_order) || 0,
      amber_threshold_pct: parseFloat(form.amber_threshold_pct) || 0.20,
      active: true,
    };
    if (editing) { await supabase.from('fridges').update(payload).eq('id', editing); }
    else         { await supabase.from('fridges').insert(payload); }
    await loadFridges(); setShowForm(false); setEditing(null); setSaving(false);
  }

  async function toggleActive(fridge) {
    await supabase.from('fridges').update({ active: !fridge.active }).eq('id', fridge.id);
    setFridges(prev => prev.map(f => f.id === fridge.id ? { ...f, active: !f.active } : f));
  }

  const grouped = fridges.reduce((acc, f) => {
    const key = `${f.venue}||${f.dept}`;
    if (!acc[key]) acc[key] = { venue: f.venue, dept: f.dept, items: [] };
    acc[key].items.push(f); return acc;
  }, {});

  const previewAmberEdge = form.max_temp !== '' && form.amber_threshold_pct !== ''
    ? (parseFloat(form.max_temp) - parseFloat(form.amber_threshold_pct) * parseFloat(form.max_temp)).toFixed(1)
    : null;

  return (
    <div style={{ padding: '1.5rem', maxWidth: '860px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <Thermometer size={20} style={{ color: C.mid }} />
          <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, color: C.charcoal }}>Fridge Management</h2>
        </div>
        <button onClick={() => { setEditing(null); setForm(blank); setShowForm(true); }} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.48rem 1rem', background: C.navy, color: C.white, border: 'none', borderRadius: '9px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
          <Plus size={14} /> Add Fridge
        </button>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {[
          { color: C.green, bg: C.greenLight, label: 'Good — in safe range' },
          { color: C.amber, bg: C.amberLight, label: 'Amber — approaching limit' },
          { color: C.red,   bg: C.redLight,   label: 'Red — out of range' },
        ].map(({ color, bg, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: color, flexShrink: 0 }} />
            <span style={{ fontSize: '0.75rem', color: C.mid }}>{label}</span>
          </div>
        ))}
      </div>

      {showForm && (
        <div style={{ background: C.white, borderRadius: '14px', padding: '1.25rem', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', marginBottom: '1.5rem', border: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: '0.92rem', color: C.charcoal }}>{editing ? 'Edit Fridge' : 'New Fridge'}</p>
            <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={16} /></button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem', marginBottom: '0.65rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Venue</label>
              <select value={form.venue} onChange={e => setForm(p => ({ ...p, venue: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
                {ALL_VENUES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Dept</label>
              <select value={form.dept} onChange={e => setForm(p => ({ ...p, dept: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
                <option value="bar">Bar</option>
                <option value="kitchen">Kitchen</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '0.65rem' }}>
            <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Name</label>
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Bar Fridge 1, Standing Freezer" style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal, boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.65rem', marginBottom: '0.9rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Type</label>
              <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.85rem', color: C.charcoal, background: C.white }}>
                <option value="fridge">Fridge</option>
                <option value="freezer">Freezer</option>
                <option value="dishwasher">Dishwasher</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Min °C</label>
              <input type="number" value={form.min_temp} onChange={e => setForm(p => ({ ...p, min_temp: e.target.value }))} placeholder="—" style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Max °C</label>
              <input type="number" value={form.max_temp} onChange={e => setForm(p => ({ ...p, max_temp: e.target.value }))} placeholder="e.g. 7" disabled={form.type === 'dishwasher'} style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal, boxSizing: 'border-box', opacity: form.type === 'dishwasher' ? 0.4 : 1 }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.28rem' }}>Amber %</label>
              <input type="number" step="0.05" min="0.05" max="0.5" value={form.amber_threshold_pct} onChange={e => setForm(p => ({ ...p, amber_threshold_pct: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal, boxSizing: 'border-box' }} />
            </div>
          </div>
          {previewAmberEdge !== null && form.type !== 'dishwasher' && (
            <div style={{ marginBottom: '0.85rem', padding: '0.45rem 0.75rem', background: C.amberLight, borderRadius: '8px', display: 'flex', gap: '1.5rem', fontSize: '0.75rem', color: C.amber, flexWrap: 'wrap' }}>
              <span>🟡 Amber zone: <strong>{previewAmberEdge}°C – {form.max_temp}°C</strong></span>
              <span>🔴 Red: above <strong>{form.max_temp}°C</strong></span>
              <span>🟢 Green: below <strong>{previewAmberEdge}°C</strong></span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.65rem' }}>
            <button onClick={handleSave} disabled={!form.name.trim() || saving} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.52rem 1.25rem', background: form.name.trim() ? C.navy : C.border, color: C.white, border: 'none', borderRadius: '9px', fontWeight: 700, fontSize: '0.88rem', cursor: form.name.trim() ? 'pointer' : 'not-allowed' }}>
              <Save size={13} /> {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Fridge'}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ padding: '0.52rem 1rem', border: `1px solid ${C.border}`, borderRadius: '9px', background: C.white, color: C.mid, fontSize: '0.88rem', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: C.mid, fontSize: '0.85rem' }}>Loading…</p>
      ) : (
        Object.values(grouped).map(({ venue, dept, items }) => (
          <div key={`${venue}||${dept}`} style={{ marginBottom: '1.75rem' }}>
            <p style={{ margin: '0 0 0.6rem', fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {venue} · {dept}
            </p>
            {items.map(fridge => {
              const tc = TYPE_CONFIG[fridge.type] || TYPE_CONFIG.fridge;
              const pct = fridge.amber_threshold_pct ?? 0.20;
              const amberEdge = fridge.max_temp != null ? (fridge.max_temp - pct * fridge.max_temp).toFixed(1) : null;
              return (
                <div key={fridge.id} style={{ background: fridge.active ? C.white : C.cream, borderRadius: '11px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '0.45rem', padding: '0.8rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', opacity: fridge.active ? 1 : 0.55 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '0.2rem', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.62rem', fontWeight: 700, color: tc.color, background: tc.bg, padding: '0.1rem 0.38rem', borderRadius: '999px', textTransform: 'uppercase' }}>{tc.label}</span>
                      <span style={{ fontSize: '0.68rem', color: C.mid }}>{rangeLabel(fridge)}</span>
                      {amberEdge && fridge.type !== 'dishwasher' && <span style={{ fontSize: '0.62rem', color: C.amber }}>amber ≥{amberEdge}°C</span>}
                      {!fridge.active && <span style={{ fontSize: '0.62rem', color: C.mid, background: C.border, padding: '0.1rem 0.38rem', borderRadius: '999px' }}>Inactive</span>}
                    </div>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.9rem', color: C.charcoal }}>{fridge.name}</p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                    <button onClick={() => startEdit(fridge)} style={{ padding: '0.3rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '7px', background: C.white, color: C.mid, fontSize: '0.75rem', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => toggleActive(fridge)} style={{ padding: '0.3rem 0.65rem', border: `1px solid ${fridge.active ? C.red + '44' : C.green + '44'}`, borderRadius: '7px', background: fridge.active ? C.redLight : C.greenLight, color: fridge.active ? C.red : C.green, fontSize: '0.75rem', cursor: 'pointer' }}>
                      {fridge.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}